import { Text } from '@latitude/text';

type LabeledItem = { label: string; description: string };

const LabeledBulletList = ({ items }: { items: LabeledItem[] }) => (
  // oxlint-disable-next-line @anchorage/tailwind/no-tailwind-important -- beats global list-style reset on raw <ul>/<ol>, load-bearing
  <ul className="list-disc !py-2.5 !pl-5">
    {items.map(({ label, description }) => (
      <li key={label}>
        <Text>
          <span className="font-bold">{label}</span>
          {description}
        </Text>
      </li>
    ))}
  </ul>
);

const AML_ITEMS = [
  {
    label: 'Written policies and procedures: ',
    description:
      'Implemented to maintain compliance with relevant AML laws and regulations.',
  },
  {
    label: 'Suspicious activity reporting: ',
    description: 'Mechanisms to detect and report suspicious activity.',
  },
  {
    label: 'Sanctions screening: ',
    description:
      'Regular checks of customers and transactions against OFAC sanctions lists and Politically Exposed Persons (PEPs).',
  },
  {
    label: 'Independent testing: ',
    description: "Periodic reviews to ensure the program's effectiveness.",
  },
  {
    label: 'Designated personnel: ',
    description: 'Individuals appointed to manage and monitor the program.',
  },
  {
    label: 'AML and sanctions training: ',
    description: 'Ongoing education for relevant staff.',
  },
  {
    label: 'Customer due diligence: ',
    description:
      'Procedures to understand and monitor customer relationships and transactions, including updates on beneficial ownership.',
  },
  {
    label: 'Record retention: ',
    description: 'Processes to retain necessary documentation.',
  },
];

export const AML_DRAWER_CONTENT = {
  title: 'Anti-money laundering program (AML)',
  content: (
    <>
      <Text>
        This program is designed to ensure ongoing compliance with anti-money
        laundering regulations. It includes:
      </Text>
      <LabeledBulletList items={AML_ITEMS} />
    </>
  ),
};

const ECP_ITEMS = [
  {
    label: 'Financial institutions: ',
    description:
      'Banks, trust companies, and broker-dealers regulated by U.S. or foreign governments.',
  },
  {
    label: 'Insurance and investment companies: ',
    description: 'Regulated entities acting for their own account.',
  },
  {
    label: 'Corporations and partnerships: ',
    description:
      'Entities with total assets exceeding $10M, or a net worth over $1M if hedging commercial risk.',
  },
  {
    label: 'Commodity pools: ',
    description: 'Entities with more than $5M in assets under management.',
  },
  {
    label: 'Government entities and employee benefit plans: ',
    description: 'Plans with assets exceeding $5M.',
  },
  {
    label: 'Entities (corporations, funds, LLCs, partnerships): ',
    description: '$10M or more in total assets.',
  },
  {
    label: 'Individuals: ',
    description:
      '$10M or more in investments, or $5M or more if trading for risk management purposes.',
  },
];

const TRADING_AUTH_ITEMS = [
  {
    label: 'Corporate/Company: ',
    description: 'Board or corporate resolution naming authorized traders.',
  },
  {
    label: 'Fund: ',
    description:
      'PPM, LPA, IMA, or offering documents confirming derivatives authority.',
  },
  {
    label: 'Trust: ',
    description: 'Trust agreement naming authorized traders or trustees.',
  },
  {
    label: 'Subsidiary/affiliate: ',
    description:
      "Parent company resolution, delegation of authority, or power of attorney naming the subsidiary's or affiliate's authorized traders.",
  },
];

export const TRADING_AUTH_DRAWER_CONTENT = {
  title: 'Derivatives trading authorization documents',
  content: (
    <>
      <Text>
        Acceptable documents depend on your organization&apos;s structure.
        Upload one document that matches your entity type below.
      </Text>
      <Text variant="bodyBold">Accepted documents by entity type</Text>
      <LabeledBulletList items={TRADING_AUTH_ITEMS} />
    </>
  ),
};

const ECP_EVIDENCE_ITEMS = [
  {
    label:
      'Financial institution, insurance company, or registered investment company: ',
    description: 'A regulatory registration or license.',
  },
  {
    label: 'Corporation, partnership, or LLC (over thresholds): ',
    description:
      'Audited financials or a balance sheet showing total assets over $10,000,000 (or net worth over $1,000,000 if hedging commercial risk).',
  },
  {
    label: 'Commodity pool: ',
    description:
      'An offering document or account statement showing total assets over $5,000,000.',
  },
  {
    label: 'Government entity or employee benefit plan: ',
    description: 'Evidence of plan assets over $5,000,000.',
  },
  {
    label: 'Individual (over thresholds): ',
    description:
      'Investment statements showing total assets over $10,000,000 (or over $5,000,000 if trading for risk management).',
  },
  {
    label: 'Other: ',
    description: 'Any documentation that supports your ECP status.',
  },
];

export const ECP_EVIDENCE_DRAWER_CONTENT = {
  title: 'Acceptable ECP evidence',
  content: (
    <>
      <Text>
        Acceptable evidence depends on your ECP category. Upload one document
        that matches your category below.
      </Text>
      <Text variant="bodyBold">Accepted documents by category</Text>
      <LabeledBulletList items={ECP_EVIDENCE_ITEMS} />
    </>
  ),
};

export const ECP_DRAWER_CONTENT = {
  title: 'Eligible Contract Participant (ECP)',
  content: (
    <>
      <Text>
        An Eligible Contract Participant is an entity or individual that meets
        specific financial thresholds under the Commodity Exchange Act, allowing
        it to enter into certain OTC derivatives contracts.
      </Text>
      <Text variant="bodyBold">Common ECP categories</Text>
      <LabeledBulletList items={ECP_ITEMS.slice(0, 5)} />
      <Text variant="bodyBold">Verification thresholds</Text>
      <LabeledBulletList items={ECP_ITEMS.slice(5, 7)} />
    </>
  ),
};
